<template>
  <div :class="classObj" class="app-wrapper">
    <Sidebar class="sidebar-container" />
    <div :class="{ hasTagsView: needTagsView }" class="main-container">
      <div :class="{ 'fixed-header': fixedHeader }">
        <Navbar />
        <Breadcrumb id="breadcrumb-container" class="breadcrumb-container" />
      </div>
      <AppMain />
      <RightPanel v-if="showSettings">
        <Settings />
      </RightPanel>
    </div>
  </div>
</template>

<script>
import RightPanel from '@/components/RightPanel';
import { AppMain, Navbar, Settings, Sidebar } from './components';
import Breadcrumb from '@/components/Breadcrumb';
import ResizeMixin from './mixin/ResizeHandler';
import { mapState } from 'vuex';
import { thirdApi } from '@/api';
import { confirmRequiredEmails } from '@/utils/required-email-notice';
import moment from 'moment';

export default {
  name: 'Layout',
  components: {
    AppMain,
    Navbar,
    RightPanel,
    Settings,
    Sidebar,
    Breadcrumb,
  },
  mixins: [ResizeMixin],
  data() {
    return {
      notificationRef: null,
      refreshTimer: null,
    };
  },
  computed: {
    ...mapState({
      sidebar: state => state.app.sidebar,
      device: state => state.app.device,
      showSettings: state => state.settings.showSettings,
      needTagsView: state => state.settings.tagsView,
      fixedHeader: state => state.settings.fixedHeader,
    }),
    classObj() {
      return {
        hideSidebar: !this.sidebar.opened,
        openSidebar: this.sidebar.opened,
        withoutAnimation: this.sidebar.withoutAnimation,
        mobile: this.device === 'mobile',
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (window.location.hash !== '#/wave') {
        this.initNotificationRefresh();
      }
    });
  },
  beforeDestroy() {
    // 清理定时器，防止内存泄漏
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    // 关闭通知
    if (this.notificationRef && !this.notificationRef.closed) {
      this.notificationRef.close();
      this.notificationRef = null;
    }
  },
  methods: {
    // 初始化通知刷新
    initNotificationRefresh() {
      const isAdmin = !this.$store.getters.cusId;
      
      // 立即执行一次
      this.fetchNotificationData(isAdmin);
      
      // 设置定时刷新（30 分钟）
      this.refreshTimer = setInterval(() => {
        this.fetchNotificationData(isAdmin);
      }, 1800000);
    },

    // 获取通知数据
    fetchNotificationData(isAdmin) {
      if (isAdmin) {
        this.getNotice();
      } else {
        this.getClientNotice();
      }
    },

    // Admin Tips =====>>>>>
    // approvalNotCount (integer, optional): 审批不通过工单数量 ,
    // logisticsQuoteConfirmedCount (integer, optional): 物流报价已确认订单数量 ,
    // pendApprovalCustomerCount (integer, optional): 待审批入场客户数量 ,
    // pendApprovalWorkCount (integer, optional): 待审批工单数量 ,
    // pendCancelCount (integer, optional): 待 HJ 取消订单数量 ,
    // pendErpClosePoCount (integer, optional): 待 erp 关闭的 po 数量 ,
    // pendLogisticsQuoteCount (integer, optional): 物流报价待提供订单数量 ,
    // pendPackageSizeCount (integer, optional): 包裹尺寸待提供订单数量 ,
    // timeoutWorkCount (integer, optional): 超时工单数量 ,
    // urgentCount (integer, optional): 加急单数量
    getNotice() {
      const deliveryTimeEnd = this.calculateDeliveryTimeEnd();
      const params = {
        deliveryTimeEnd,
        section: this.$store.getters.userInfo.deptAbbr
      };

      thirdApi.getDashoardAdminData(params).then(res => {
        const notificationItems = this.buildAdminNotificationItems(res);
        this.handleNotificationDisplay(notificationItems);
      }).catch(error => {
        console.error('获取管理员通知数据失败:', error);
      });
    },

    // 计算 Admin 数据时间范围
    calculateDeliveryTimeEnd() {
      const now = moment().day();
      const start = new Date();
      
      if ([1, 2].includes(now)) {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 4);
        return moment(start).format('YYYY-MM-DD HH:mm:ss');
      } else if ([3, 4, 5].includes(now)) {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 2);
        return moment(start).format('YYYY-MM-DD HH:mm:ss');
      } else if ([6].includes(now)) {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 2);
        return moment(start).format('YYYY-MM-DD 00:00:00');
      } else {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 3);
        return moment(start).format('YYYY-MM-DD 00:00:00');
      }
    },

    // 构建 Admin 通知项
    buildAdminNotificationItems(res) {
      const items = [];
      const hasPermission = (btn) => this.$store.getters.userPermissionBtns.includes(btn);

      if (hasPermission('businessabnormalorderurgent') && res.urgentCount > 0) {
        items.push({ label: this.$t('Pendingurgentorders'), value: res.urgentCount });
      }
      if (hasPermission('businessabnormalorderpendinghjcancel') && res.pendCancelCount > 0) {
        items.push({ label: this.$t('PendingHJcancelorders'), value: res.pendCancelCount });
      }
      if (hasPermission('businesspurchaseordercsgadminindex') && res.pendErpClosePoCount > 0) {
        items.push({ label: this.$t('pohasnotclosed'), value: res.pendErpClosePoCount });
      }
      
      if (hasPermission('businessabnormalorderwairing')) {
        if (res.pendPackageSizeCount > 0) {
          items.push({ 
            label: this.$t('PackageDimensionsRequired'), 
            value: res.pendPackageSizeCount,
            link: '/#/business/csgOrder/abnormalOrders/WaitingShipped'
          });
        }
        if (res.pendLogisticsQuoteCount > 0) {
          items.push({ 
            label: this.$t('ShippingQuoteRequired'), 
            value: res.pendLogisticsQuoteCount,
            link: '/#/business/csgOrder/abnormalOrders/WaitingShipped'
          });
        }
        if (res.logisticsQuoteConfirmedCount > 0) {
          items.push({ 
            label: this.$t('FreightConfirmed'), 
            value: res.logisticsQuoteConfirmedCount,
            link: '/#/business/csgOrder/abnormalOrders/WaitingShipped'
          });
        }
      }
      
      if (hasPermission('businessopenworkorderlist')) {
        if (res.approvalNotCount > 0) {
          items.push({ label: this.$t('RejectedTicket'), value: res.approvalNotCount });
        }
        if (res.pendApprovalWorkCount > 0) {
          items.push({ label: this.$t('ApprovalPending'), value: res.pendApprovalWorkCount });
        }
        if (res.timeoutWorkCount > 0) {
          items.push({ label: this.$t('OverdueTicket'), value: res.timeoutWorkCount });
        }
      }
      
      if (hasPermission('onboardingpageindex') && res.pendApprovalCustomerCount > 0) {
        items.push({ label: this.$t('ApprovalsOnboarding'), value: res.pendApprovalCustomerCount });
      }

      return items;
    },

    // User Tips =====>>>>>
    // abnormalCount (integer, optional): 异常订单数量 ,
    // approvalNotCount (integer, optional): 审批不通过工单数量 ,
    // outOfStockCount (integer, optional): 缺货订单数量 ,
    // packageProvidedCount (integer, optional): 包裹尺寸已提供工单数量 ,
    // quoteProvidedCount (integer, optional): 物流报价已提供订单数量
    getClientNotice() {
      confirmRequiredEmails(true);
      thirdApi.getDashoardUserData().then(res => {
        const notificationItems = this.buildClientNotificationItems(res);
        this.handleNotificationDisplay(notificationItems);
      }).catch(error => {
        console.error('获取用户通知数据失败:', error);
      });
    },

    // 构建 Client 通知项
    buildClientNotificationItems(res) {
      const items = [];
      const hasPermission = (btn) => this.$store.getters.userPermissionBtns.includes(btn);

      if (hasPermission('businessabnormalorderanomaly') && res.abnormalCount > 0) {
        items.push({ label: this.$t('route.BusinessAbnormalOrderANOMALY'), value: res.abnormalCount });
      }
      if (hasPermission('businessabnormalorderoutofstock') && res.outOfStockCount > 0) {
        items.push({ label: this.$t('route.BusinessAbnormalOrderOUTOFSTOCK'), value: res.outOfStockCount });
      }
      
      if (hasPermission('businessabnormalorderwairing')) {
        if (res.packageProvidedCount > 0) {
          items.push({ 
            label: this.$t('PackageDimensionsProvided'), 
            value: res.packageProvidedCount,
            link: '/#/business/csgOrder/abnormalOrders/WaitingShipped'
          });
        }
        if (res.quoteProvidedCount > 0) {
          items.push({ 
            label: this.$t('FreightProvided'), 
            value: res.quoteProvidedCount,
            link: '/#/business/csgOrder/abnormalOrders/WaitingShipped'
          });
        }
      }
      
      if (hasPermission('businessopenworkorderlist') && res.approvalNotCount > 0) {
        items.push({ label: this.$t('RejectedTicket'), value: res.approvalNotCount });
      }

      return items;
    },

    // 处理通知显示
    handleNotificationDisplay(items) {
      if (!items || items.length === 0) {
        this.closeNotification();
        return;
      }

      const html = this.buildNotificationHtml(items);
      this.showOrUpdateNotification(html);
    },

    // 构建通知 HTML
    buildNotificationHtml(items) {
      let html = '<div style="color: teal">';
      items.forEach(item => {
        if (item.link) {
          html += `<div><a href="${item.link}" target="_blank">${item.label}：${item.value}</a></div>`;
        } else {
          html += `<div>${item.label}：${item.value}</div>`;
        }
      });
      html += '</div>';
      return html;
    },

    // 关闭通知
    closeNotification() {
      if (this.notificationRef && !this.notificationRef.closed) {
        this.notificationRef.close();
        this.notificationRef = null;
      }
    },

    // 显示或更新通知
    showOrUpdateNotification(html) {
      if (this.notificationRef && !this.notificationRef.closed) {
        this.updateExistingNotification(html);
      } else {
        this.createNewNotification(html);
      }
    },

    // 更新通知
    updateExistingNotification(html) {
      // 先关闭旧的通知，然后创建新的
      this.closeNotification();
      this.createNewNotification(html);
    },

    // 创建通知
    createNewNotification(html) {
      this.notificationRef = this.$notify({
        title: this.$t('toast.hintText'),
        dangerouslyUseHTMLString: true,
        message: html,
        offset: 60,
        position: 'bottom-right',
        customClass: 'layout-order-notification',
        duration: 0,
        onClose: () => {
          this.notificationRef = null;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/mixin.scss';
@import '~@/styles/variables.scss';

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - #{$sideBarWidth});
  transition: width 0.28s;
  background-color: #fff;
  box-shadow: 0 2px 6px 0 rgba(149, 149, 149, 0.3);
}

.hideSidebar .fixed-header {
  width: calc(100% - #{$sideBarWidthHide});
}

.mobile .fixed-header {
  width: 100%;
}
</style>
